/**
 * Mystery Box buy box
 * Pool: product metafield custom.mystery_box_pool
 * Color order/whitelist: custom.mystery_box_color_priority (list order = priority)
 * Only broken colorways with qty > 0 for the selected size.
 * ATC mirrors theme.AjaxProduct → theme.cart.addItemFromForm
 * → body "added.ajaxProduct" → CartDrawer.buildCart(..., true) → drawer.open()
 */
class MysteryBoxForm extends HTMLElement {
  connectedCallback() {
    if (this._mysteryReady) return;
    this._mysteryReady = true;

    this.requiredCount = Number(this.dataset.requiredCount || 6);

    this.pool = this.readJson("[data-mystery-pool]");
    this.boxVariants = this.readJson("[data-box-variants]");
    this.colorPriority = this.normalizeColorPriority(
      this.readJson("[data-mystery-color-priority]"),
    );

    this.form = this.querySelector("form");
    this.sizeInputs = Array.from(
      this.querySelectorAll('input[type="radio"][data-mystery-size]'),
    );
    this.variantSelect = this.querySelector("[data-mystery-variant-select]");
    this.propsInput = this.querySelector("[data-mystery-props]");
    this.uidInput = this.querySelector("[data-mystery-uid]");
    this.qtyInput = this.querySelector("[data-mystery-qty]");
    this.addButton = this.querySelector("[data-mystery-add]");
    this.addButtonText = this.querySelector("[data-mystery-add-text]");
    this.errorsEl = this.querySelector("[data-mystery-errors]");

    this.sizeAvailability = this.computeSizeAvailability();
    this.applySizeAvailability();
    this.bindEvents();
    this.syncFromCheckedSize();
  }

  readJson(selector) {
    const el = this.querySelector(selector);
    if (!el) return [];
    try {
      return JSON.parse(el.textContent.trim() || "[]");
    } catch (e) {
      console.warn("MysteryBoxForm: invalid JSON", selector, e);
      return [];
    }
  }

  normalizeSize(value) {
    return String(value || "")
      .trim()
      .toUpperCase()
      .replace(/['']/g, "")
      .replace(/\s+/g, "");
  }

  normalizeColor(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
  }

  normalizeColorPriority(list) {
    if (!Array.isArray(list)) return [];
    const seen = new Set();
    const ordered = [];
    list.forEach((item) => {
      const key = this.normalizeColor(item);
      if (!key || seen.has(key)) return;
      seen.add(key);
      ordered.push(key);
    });
    return ordered;
  }

  isColorAllowed(colorKey) {
    if (!this.colorPriority.length) return true;
    return this.colorPriority.indexOf(colorKey) !== -1;
  }

  isInStock(qty) {
    const n = Number(qty);
    return Number.isFinite(n) && n > 0;
  }

  isBrokenColorway(variants) {
    if (!variants || !variants.length) return false;
    let hasPositive = false;
    let hasZeroOrLess = false;
    for (let i = 0; i < variants.length; i++) {
      const n = Number(variants[i].inventory_quantity);
      if (!Number.isFinite(n)) continue;
      if (n > 0) hasPositive = true;
      else hasZeroOrLess = true;
      if (hasPositive && hasZeroOrLess) return true;
    }
    return false;
  }

  getBrokenColorsForProduct(product) {
    const byColor = {};
    (product.variants || []).forEach((variant) => {
      const colorKey = this.normalizeColor(variant.color);
      if (!byColor[colorKey]) byColor[colorKey] = [];
      byColor[colorKey].push(variant);
    });

    const broken = new Set();
    Object.keys(byColor).forEach((colorKey) => {
      if (!this.isColorAllowed(colorKey)) return;
      if (this.isBrokenColorway(byColor[colorKey])) broken.add(colorKey);
    });
    return broken;
  }

  shuffle(list) {
    const arr = list.slice();
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
    return arr;
  }

  pickRandom(list) {
    if (!list || !list.length) return null;
    return list[Math.floor(Math.random() * list.length)];
  }

  computeSizeAvailability() {
    const map = {};

    this.sizeInputs.forEach((input) => {
      const sizeKey = this.normalizeSize(input.value);
      map[sizeKey] = { count: 0, candidates: [] };
    });

    this.pool.forEach((product) => {
      if (!product || !Array.isArray(product.variants)) return;
      const brokenColors = this.getBrokenColorsForProduct(product);
      if (!brokenColors.size) return;

      // sizeKey -> colorKey -> [variantIds]
      const bySizeColor = {};

      product.variants.forEach((variant) => {
        const sizeKey = this.normalizeSize(variant.size);
        if (!sizeKey || !map[sizeKey]) return;
        if (!this.isInStock(variant.inventory_quantity)) return;

        const colorKey = this.normalizeColor(variant.color);
        if (!brokenColors.has(colorKey)) return;

        if (!bySizeColor[sizeKey]) bySizeColor[sizeKey] = {};
        if (!bySizeColor[sizeKey][colorKey]) bySizeColor[sizeKey][colorKey] = [];
        bySizeColor[sizeKey][colorKey].push(variant.id);
      });

      Object.keys(bySizeColor).forEach((sizeKey) => {
        map[sizeKey].candidates.push({
          productId: product.id,
          byColor: bySizeColor[sizeKey],
        });
        map[sizeKey].count += 1;
      });
    });

    return map;
  }

  applySizeAvailability() {
    this.sizeInputs.forEach((input) => {
      const sizeKey = this.normalizeSize(input.value);
      const info = this.sizeAvailability[sizeKey] || { count: 0 };
      const available = info.count >= this.requiredCount;
      const wrap = input.closest(".variant-input");
      const label = wrap && wrap.querySelector("label");

      input.classList.toggle("disabled", !available);
      input.disabled = !available;
      input.setAttribute("data-pool-count", String(info.count));

      if (label) label.classList.toggle("disabled", !available);
      if (!available && input.checked) input.checked = false;
    });

    const checked = this.sizeInputs.find((i) => i.checked && !i.disabled);
    if (!checked) {
      const firstAvailable = this.sizeInputs.find((i) => !i.disabled);
      if (firstAvailable) firstAvailable.checked = true;
    }
  }

  bindEvents() {
    this.sizeInputs.forEach((input) => {
      input.addEventListener("change", () => {
        this.clearErrors();
        this.syncFromCheckedSize();
      });
    });

    if (this.form) {
      this.form.addEventListener("submit", (e) => {
        e.preventDefault();
        this.handleAddToCart();
      });
    }
  }

  getCheckedSizeInput() {
    return this.sizeInputs.find((i) => i.checked && !i.disabled) || null;
  }

  findBoxVariantId(sizeValue) {
    const sizeKey = this.normalizeSize(sizeValue);
    const match = this.boxVariants.find(
      (v) => this.normalizeSize(v.size) === sizeKey,
    );
    return match ? match.id : null;
  }

  pickFulfillmentIds(sizeValue) {
    const sizeKey = this.normalizeSize(sizeValue);
    const info = this.sizeAvailability[sizeKey];
    if (!info || !info.candidates || !info.candidates.length) return [];

    const picked = [];
    const usedProducts = new Set();
    const colorOrder = this.colorPriority.length
      ? this.colorPriority
      : this.shuffle(
          Array.from(
            new Set(
              info.candidates.flatMap((c) => Object.keys(c.byColor || {})),
            ),
          ),
        );

    for (let c = 0; c < colorOrder.length && picked.length < this.requiredCount; c++) {
      const colorKey = colorOrder[c];
      const products = this.shuffle(info.candidates);

      for (let p = 0; p < products.length && picked.length < this.requiredCount; p++) {
        const candidate = products[p];
        if (usedProducts.has(candidate.productId)) continue;
        const ids = candidate.byColor && candidate.byColor[colorKey];
        if (!ids || !ids.length) continue;

        const variantId = this.pickRandom(ids);
        if (variantId == null) continue;

        picked.push(variantId);
        usedProducts.add(candidate.productId);
      }
    }

    return picked;
  }

  clearErrors() {
    if (this.form) {
      this.form.querySelectorAll(".errors").forEach((el) => {
        if (el.hasAttribute("data-mystery-errors")) {
          el.hidden = true;
          el.textContent = "";
        } else {
          el.remove();
        }
      });
    }
    if (this.errorsEl) {
      this.errorsEl.hidden = true;
      this.errorsEl.textContent = "";
    }
  }

  showError(message) {
    if (!this.errorsEl) {
      console.warn(message);
      return;
    }
    this.errorsEl.hidden = false;
    this.errorsEl.textContent = message;
  }

  syncFromCheckedSize() {
    const input = this.getCheckedSizeInput();
    const soldOutLabel =
      (window.theme && theme.productStrings && theme.productStrings.soldOut) ||
      "Sold Out";

    if (!input) {
      if (this.variantSelect) this.variantSelect.value = "";
      if (this.propsInput) this.propsInput.value = "";
      if (this.addButton) {
        this.addButton.disabled = true;
        if (this.addButtonText) this.addButtonText.textContent = soldOutLabel;
      }
      return;
    }

    const boxVariantId = this.findBoxVariantId(input.value);
    const fulfillmentIds = this.pickFulfillmentIds(input.value);

    if (this.variantSelect && boxVariantId) {
      this.variantSelect.value = String(boxVariantId);
    }
    if (this.propsInput) {
      this.propsInput.value = fulfillmentIds.join(",");
    }

    const ready =
      Boolean(boxVariantId) && fulfillmentIds.length >= this.requiredCount;

    if (this.addButton) {
      this.addButton.disabled = !ready;
      if (this.addButtonText) {
        this.addButtonText.textContent = ready ? "ADD TO CART" : soldOutLabel;
      }
    }
  }

  setLoading(isLoading) {
    if (!this.addButton) return;
    this.addButton.classList.toggle("btn--loading", isLoading);
    if (isLoading) this.addButton.disabled = true;
  }

  handleAddToCart() {
    if (this._adding) return;
    if (!window.jQuery || !window.theme || !theme.cart) {
      this.showError("Cart is not ready yet. Please refresh the page.");
      return;
    }

    this.clearErrors();
    this.syncFromCheckedSize();

    const input = this.getCheckedSizeInput();
    if (!input) {
      this.showError("Please select an available size.");
      return;
    }

    const boxVariantId = this.findBoxVariantId(input.value);
    const fulfillmentIds = this.pickFulfillmentIds(input.value);

    if (!boxVariantId || fulfillmentIds.length < this.requiredCount) {
      this.showError("This size is not available right now.");
      return;
    }

    // Keep form fields in sync for serialize() — same as theme.AjaxProduct
    if (this.variantSelect) this.variantSelect.value = String(boxVariantId);
    if (this.propsInput) this.propsInput.value = fulfillmentIds.join(",");
    // Unique line key so a second ATC becomes a separate line (qty 1), not qty 2
    if (this.uidInput) {
      this.uidInput.value =
        Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
    }
    if (this.qtyInput) this.qtyInput.value = "1";

    const $ = window.jQuery;
    const $form = $(this.form);
    const serialized = $form.serialize();

    this._adding = true;
    this.setLoading(true);

    theme.cart
      .addItemFromForm(serialized)
      .then(
        function () {
          this._adding = false;
          this.setLoading(false);
          this.syncFromCheckedSize();

          // Exact same success path as theme.AjaxProduct.success
          $form.find(".errors").remove();
          $("body").trigger("added.ajaxProduct");
          $(".cart-popup").addClass("active");
        }.bind(this),
      )
      .catch(
        function (err) {
          this._adding = false;
          this.setLoading(false);
          this.syncFromCheckedSize();

          var message =
            (err &&
              err.responseJSON &&
              (err.responseJSON.description || err.responseJSON.message)) ||
            "Could not add Mystery Box to cart. On Mystery Box variants enable “Continue selling when out of stock” (inventory is 0).";

          this.showError(message);
          console.warn(message, err);
        }.bind(this),
      );
  }
}

customElements.get("mystery-box-buy-box") ||
  customElements.define("mystery-box-buy-box", MysteryBoxForm);
